<template>
  <head>
    <meta charset="utf-8" />
    <title>CBHI | Community-Based Health Insurance</title>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta content="Keep your finances and membership up to date" name="description" />
    <meta content="Kelvin Njuguna Ndungu" name="author" />
    <!-- App favicon -->
    <link rel="shortcut icon" href="assets/images/favicon.svg">

    <!-- Bootstrap Css -->
    <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
    <!-- Icons Css -->
    <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />

    <!-- Bootstrap select styling -->
    <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-select@1.13.14/dist/css/bootstrap-select.min.css">

    <!-- App Css-->
    <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
    <!-- custom Css-->
    <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
  </head>

  <body data-sidebar="dark">
  <!-- Begin page -->
  <div id="layout-wrapper">
    <!-- ========== Left Sidebar Start ========== -->
    <NavigationBar/>
    <!-- Left Sidebar End -->

    <!-- ============================================================== -->
    <!-- Start right Content here -->
    <!-- ============================================================== -->
    <div class="main-content">
      <div class="page-content">
        <div class="container-fluid">
          <!-- start page title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Hospital Registration</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript: void(0);">New Hospital Form</a></li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <div class="row justify-content-center align-items-center d-flex">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-white pt-0 pr-0 p-0 d-flex justify-content-between align-items-center w-100 border-bottom">
                  <div class="btn-toolbar p-3 d-flex flex-column w-100" role="toolbar">
                    <h4 class="card-title text-uppercase mb-1">
                      Hospital Registration for CBHI
                    </h4>
                    <p class="pb-0 mb-0">Register healthcare facilities to participate in the Community-Based Health Insurance (CBHI) program. All registered hospitals will be authorized to provide services to CBHI members.</p>
                  </div>
                </div>

                <!-- Alert Messages -->
                <div class="card-body">
                  <div v-if="alert.success" class="alert alert-success alert-dismissible fade show" role="alert">
                    <i class="mdi mdi-check-all me-2"></i>
                    {{ message }}
                    <button type="button" class="btn-close" @click="clearAlerts"></button>
                  </div>

                  <div v-if="alert.info" class="alert alert-info alert-dismissible fade show" role="alert">
                    <i class="mdi mdi-information me-2"></i>
                    {{ message }}
                    <button type="button" class="btn-close" @click="clearAlerts"></button>
                  </div>

                  <div v-if="alert.failed" class="alert alert-danger alert-dismissible fade show" role="alert">
                    <i class="mdi mdi-alert-outline me-2"></i>
                    {{ message }}
                    <button type="button" class="btn-close" @click="clearAlerts"></button>
                  </div>

                  <div class="row">
                    <!-- Basic Hospital Information -->
                    <div class="col-12">
                      <div class="bg-primary border-2 bg-soft p-3 mb-4 d-flex align-items-center">
                        <i class="mdi mdi-hospital-building me-2 fs-5"></i>
                        <p class="fw-semibold mb-0 pb-0 text-uppercase">Hospital Information</p>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-6 col-lg-6 mb-3">
                      <div class="mb-3">
                        <label for="hospital-name" class="form-label">Hospital Name <strong class="text-danger fw-bold">*</strong></label>
                        <input v-model="hospitalForm.hospitalName" type="text" class="form-control" id="hospital-name" placeholder="Enter Hospital Name" spellcheck="false" required>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-6 col-lg-6 mb-3">
                      <div class="mb-3">
                        <label for="hospital-license" class="form-label">License Number <strong class="text-danger fw-bold">*</strong></label>
                        <div class="input-group">
                          <input
                              v-model="hospitalForm.licenseNumber"
                              @blur="checkLicenseAvailability"
                              type="text"
                              class="form-control"
                              :class="{
                              'is-valid': validationStates.licenseNumber === 'valid',
                              'is-invalid': validationStates.licenseNumber === 'invalid'
                            }"
                              id="hospital-license"
                              placeholder="Enter Hospital License Number"
                              spellcheck="false"
                              required>
                          <span v-if="validationStates.licenseNumber === 'checking'" class="input-group-text">
                            <span class="spinner-border spinner-border-sm" role="status"></span>
                          </span>
                          <span v-else-if="validationStates.licenseNumber === 'valid'" class="input-group-text text-success">
                            <i class="mdi mdi-check-circle"></i>
                          </span>
                          <span v-else-if="validationStates.licenseNumber === 'invalid'" class="input-group-text text-danger">
                            <i class="mdi mdi-close-circle"></i>
                          </span>
                        </div>
                        <div v-if="validationStates.licenseNumber === 'valid'" class="valid-feedback d-block">
                          <i class="mdi mdi-check-circle me-1"></i>{{ validationMessages.licenseNumber }}
                        </div>
                        <div v-if="validationStates.licenseNumber === 'invalid'" class="invalid-feedback d-block">
                          <i class="mdi mdi-alert-circle me-1"></i>{{ validationMessages.licenseNumber }}
                        </div>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-4 col-lg-4 mb-3">
                      <div class="mb-3">
                        <label for="hospital-category" class="form-label">Hospital Category <strong class="text-danger fw-bold">*</strong></label>
                        <select v-model="hospitalForm.categoryId" class="form-select" id="hospital-category" required>
                          <option value="">Select Hospital Category</option>
                          <option v-for="category in hospitalCategories" :key="category.category_id" :value="category.category_id">
                            {{ category.category_name }}
                          </option>
                        </select>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-4 col-lg-4 mb-3">
                      <div class="mb-3">
                        <label for="ownership-type" class="form-label">Ownership Type <strong class="text-danger fw-bold">*</strong></label>
                        <select v-model="hospitalForm.ownershipId" class="form-select" id="ownership-type" required>
                          <option value="">Select Ownership</option>
                          <option v-for="ownership in ownershipTypes" :key="ownership.ownership_id" :value="ownership.ownership_id">
                            {{ ownership.ownership_name }}
                          </option>
                        </select>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-4 col-lg-4 mb-3">
                      <div class="mb-3">
                        <label for="bed-capacity" class="form-label">Bed Capacity</label>
                        <input v-model.number="hospitalForm.bedCapacity" type="number" class="form-control" id="bed-capacity" placeholder="Number of Beds" min="0">
                      </div>
                    </div>

                    <!-- Location Information -->
                    <div class="col-12">
                      <div class="bg-info border-2 bg-soft p-3 mb-4 d-flex align-items-center">
                        <i class="mdi mdi-map-marker me-2 fs-5"></i>
                        <p class="fw-semibold mb-0 pb-0 text-uppercase">Location Information</p>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-3 mb-3">
                      <div class="mb-3">
                        <label for="county-input" class="form-label">County <strong class="text-danger fw-bold">*</strong></label>
                        <select v-model="hospitalForm.countyId" @change="loadSubCounties" id="county-input" class="form-select" required>
                          <option value="">Select County</option>
                          <option v-for="county in counties" :key="county.county_id" :value="county.county_id">
                            {{ county.county_name }}
                          </option>
                        </select>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-3 mb-3">
                      <div class="mb-3">
                        <label for="subcounty-input" class="form-label">Sub-County <strong class="text-danger fw-bold">*</strong></label>
                        <select v-model="hospitalForm.subCountyId" @change="loadWards" id="subcounty-input" class="form-select" required :disabled="!hospitalForm.countyId">
                          <option value="">Select Sub-County</option>
                          <option v-for="subCounty in subCounties" :key="subCounty.sub_county_id" :value="subCounty.sub_county_id">
                            {{ subCounty.sub_county_name }}
                          </option>
                        </select>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-3 mb-3">
                      <div class="mb-3">
                        <label for="ward-input" class="form-label">Ward <strong class="text-danger fw-bold">*</strong></label>
                        <select v-model="hospitalForm.wardId" @change="loadLocations" id="ward-input" class="form-select" required :disabled="!hospitalForm.subCountyId">
                          <option value="">Select Ward</option>
                          <option v-for="ward in wards" :key="ward.ward_id" :value="ward.ward_id">
                            {{ ward.ward_name }}
                          </option>
                        </select>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-3 mb-3">
                      <div class="mb-3">
                        <label for="location-input" class="form-label">Location</label>
                        <select v-model="hospitalForm.locationId" id="location-input" class="form-select" :disabled="!hospitalForm.wardId">
                          <option value="">Select Location</option>
                          <option v-for="location in locations" :key="location.location_id" :value="location.location_id">
                            {{ location.location_name }}
                          </option>
                        </select>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-6 mb-3">
                      <div class="mb-3">
                        <label for="address-input" class="form-label">Physical Address <strong class="text-danger fw-bold">*</strong></label>
                        <textarea v-model="hospitalForm.physicalAddress" class="form-control" id="address-input" rows="2" placeholder="Enter detailed physical address" required></textarea>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-6 mb-3">
                      <div class="mb-3">
                        <label for="postal-address" class="form-label">Postal Address</label>
                        <textarea v-model="hospitalForm.postalAddress" class="form-control" id="postal-address" rows="2" placeholder="P.O. Box, City"></textarea>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-3 mb-3">
                      <div class="mb-3">
                        <label for="postal-code" class="form-label">Postal Code</label>
                        <input v-model="hospitalForm.postalCode" type="text" class="form-control" id="postal-code" placeholder="e.g., 00100">
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-3 mb-3">
                      <div class="mb-3">
                        <label for="po-box" class="form-label">P.O. Box</label>
                        <input v-model="hospitalForm.poBox" type="text" class="form-control" id="po-box" placeholder="e.g., 12345">
                      </div>
                    </div>

                    <!-- Map Location Section -->
                    <div class="col-12">
                      <div class="bg-warning border-2 bg-soft p-3 mb-4 d-flex align-items-center justify-content-between">
                        <div class="d-flex align-items-center">
                          <i class="mdi mdi-map-marker-radius me-2 fs-5"></i>
                          <p class="fw-semibold mb-0 pb-0 text-uppercase">Map Location</p>
                        </div>
                        <button type="button" class="btn btn-outline-primary btn-sm" @click="showMapModal">
                          <i class="mdi mdi-map-marker-plus me-2"></i>Select on Map
                        </button>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-3 mb-3">
                      <div class="mb-3">
                        <label for="latitude" class="form-label">Latitude</label>
                        <input
                            v-model.number="hospitalForm.latitude"
                            type="number"
                            step="any"
                            class="form-control"
                            id="latitude"
                            placeholder="e.g., -1.2921"
                            readonly>
                        <small class="text-muted">Click "Select on Map" to pick location</small>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-3 mb-3">
                      <div class="mb-3">
                        <label for="longitude" class="form-label">Longitude</label>
                        <input
                            v-model.number="hospitalForm.longitude"
                            type="number"
                            step="any"
                            class="form-control"
                            id="longitude"
                            placeholder="e.g., 36.8219"
                            readonly>
                        <small class="text-muted">Click "Select on Map" to pick location</small>
                      </div>
                    </div>

                    <div class="col-sm-12 col-md-6 mb-3" v-if="hospitalForm.latitude && hospitalForm.longitude">
                      <div class="mb-3">
                        <label class="form-label">Selected Location</label>
                        <div class="alert alert-success d-flex align-items-center">
                          <i class="mdi mdi-map-marker me-2"></i>
                          <span>{{ selectedLocationAddress || `${hospitalForm.latitude.toFixed(6)}, ${hospitalForm.longitude.toFixed(6)}` }}</span>
                          <button type="button" class="btn btn-sm btn-outline-primary ms-auto" @click="showMapModal">
                            <i class="mdi mdi-pencil"></i>
                          </button>
                        </div>
                      </div>
                    </div>

                    <!-- Contact Information -->
                    <div class="col-12">
                      <div class="bg-success border-2 bg-soft p-3 mb-4 d-flex align-items-center">
                        <i class="mdi mdi-phone me-2 fs-5"></i>
                        <p class="fw-semibold mb-0 pb-0 text-uppercase">Contact Information</p>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-4 mb-3">
                      <div class="mb-3">
                        <label for="phone-primary" class="form-label">Primary Phone <strong class="text-danger fw-bold">*</strong></label>
                        <div class="input-group">
                          <input
                              v-model="hospitalForm.primaryPhone"
                              @blur="() => checkPhoneAvailability(false)"
                              type="tel"
                              class="form-control"
                              :class="{
                              'is-valid': validationStates.primaryPhone === 'valid',
                              'is-invalid': validationStates.primaryPhone === 'invalid'
                            }"
                              id="phone-primary"
                              placeholder="e.g., 0712 345678"
                              required>
                          <span v-if="validationStates.primaryPhone === 'checking'" class="input-group-text">
                            <span class="spinner-border spinner-border-sm" role="status"></span>
                          </span>
                          <span v-else-if="validationStates.primaryPhone === 'valid'" class="input-group-text text-success">
                            <i class="mdi mdi-check-circle"></i>
                          </span>
                          <span v-else-if="validationStates.primaryPhone === 'invalid'" class="input-group-text text-danger">
                            <i class="mdi mdi-close-circle"></i>
                          </span>
                        </div>
                        <div v-if="validationStates.primaryPhone === 'valid'" class="valid-feedback d-block">
                          <i class="mdi mdi-check-circle me-1"></i>{{ validationMessages.primaryPhone }}
                        </div>
                        <div v-if="validationStates.primaryPhone === 'invalid'" class="invalid-feedback d-block">
                          <i class="mdi mdi-alert-circle me-1"></i>{{ validationMessages.primaryPhone }}
                        </div>
                        <small v-if="!validationMessages.primaryPhone" class="text-muted">Enter phone number with country code or local format</small>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-4 mb-3">
                      <div class="mb-3">
                        <label for="phone-secondary" class="form-label">Secondary Phone</label>
                        <div class="input-group">
                          <input
                              v-model="hospitalForm.secondaryPhone"
                              @blur="() => checkPhoneAvailability(true)"
                              type="tel"
                              class="form-control"
                              :class="{
                              'is-valid': validationStates.secondaryPhone === 'valid',
                              'is-invalid': validationStates.secondaryPhone === 'invalid'
                            }"
                              id="phone-secondary"
                              placeholder="e.g., 0722 654321">
                          <span v-if="validationStates.secondaryPhone === 'checking'" class="input-group-text">
                            <span class="spinner-border spinner-border-sm" role="status"></span>
                          </span>
                          <span v-else-if="validationStates.secondaryPhone === 'valid'" class="input-group-text text-success">
                            <i class="mdi mdi-check-circle"></i>
                          </span>
                          <span v-else-if="validationStates.secondaryPhone === 'invalid'" class="input-group-text text-danger">
                            <i class="mdi mdi-close-circle"></i>
                          </span>
                        </div>
                        <div v-if="validationStates.secondaryPhone === 'valid'" class="valid-feedback d-block">
                          <i class="mdi mdi-check-circle me-1"></i>{{ validationMessages.secondaryPhone }}
                        </div>
                        <div v-if="validationStates.secondaryPhone === 'invalid'" class="invalid-feedback d-block">
                          <i class="mdi mdi-alert-circle me-1"></i>{{ validationMessages.secondaryPhone }}
                        </div>
                        <small v-if="!validationMessages.secondaryPhone" class="text-muted">Optional alternative phone number</small>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-4 mb-3">
                      <div class="mb-3">
                        <label for="email-hospital" class="form-label">Hospital Email <strong class="text-danger fw-bold">*</strong></label>
                        <div class="input-group">
                          <input
                              v-model="hospitalForm.hospitalEmail"
                              @blur="checkEmailAvailability"
                              type="email"
                              class="form-control"
                              :class="{
                              'is-valid': validationStates.hospitalEmail === 'valid',
                              'is-invalid': validationStates.hospitalEmail === 'invalid'
                            }"
                              id="email-hospital"
                              placeholder="hospital@example.com"
                              required>
                          <span v-if="validationStates.hospitalEmail === 'checking'" class="input-group-text">
                            <span class="spinner-border spinner-border-sm" role="status"></span>
                          </span>
                          <span v-else-if="validationStates.hospitalEmail === 'valid'" class="input-group-text text-success">
                            <i class="mdi mdi-check-circle"></i>
                          </span>
                          <span v-else-if="validationStates.hospitalEmail === 'invalid'" class="input-group-text text-danger">
                            <i class="mdi mdi-close-circle"></i>
                          </span>
                        </div>
                        <div v-if="validationStates.hospitalEmail === 'valid'" class="valid-feedback d-block">
                          <i class="mdi mdi-check-circle me-1"></i>{{ validationMessages.hospitalEmail }}
                        </div>
                        <div v-if="validationStates.hospitalEmail === 'invalid'" class="invalid-feedback d-block">
                          <i class="mdi mdi-alert-circle me-1"></i>{{ validationMessages.hospitalEmail }}
                        </div>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-4 mb-3">
                      <div class="mb-3">
                        <label for="website" class="form-label">Website</label>
                        <input v-model="hospitalForm.website" type="url" class="form-control" id="website" placeholder="https://hospital.com">
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-4 mb-3">
                      <div class="mb-3">
                        <label for="contact-person" class="form-label">Contact Person <strong class="text-danger fw-bold">*</strong></label>
                        <input v-model="hospitalForm.contactPerson" type="text" class="form-control" id="contact-person" placeholder="Dr. John Doe" required>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-4 mb-3">
                      <div class="mb-3">
                        <label for="contact-position" class="form-label">Contact Person Position</label>
                        <input v-model="hospitalForm.contactPersonPosition" type="text" class="form-control" id="contact-position" placeholder="Medical Director">
                      </div>
                    </div>

                    <!-- Banking Information Section -->
                    <div class="col-12">
                      <div class="bg-info border-2 bg-soft p-3 mb-4 d-flex align-items-center">
                        <i class="mdi mdi-bank me-2 fs-5"></i>
                        <p class="fw-semibold mb-0 pb-0 text-uppercase">Banking Information</p>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-4 mb-3">
                      <div class="mb-3">
                        <label for="bank-name" class="form-label">Bank Name <strong class="text-danger fw-bold">*</strong></label>
                        <input v-model="hospitalForm.bankName" type="text" class="form-control" id="bank-name" placeholder="Enter Bank Name" required>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-4 mb-3">
                      <div class="mb-3">
                        <label for="bank-account" class="form-label">Bank Account Number <strong class="text-danger fw-bold">*</strong></label>
                        <input v-model="hospitalForm.bankAccountNumber" type="text" class="form-control" id="bank-account" placeholder="Enter Account Number" required>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-4 mb-3">
                      <div class="mb-3">
                        <label for="account-holder" class="form-label">Account Holder Name <strong class="text-danger fw-bold">*</strong></label>
                        <input v-model="hospitalForm.accountHolderName" type="text" class="form-control" id="account-holder" placeholder="Account Holder Name" required>
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-4 mb-3">
                      <div class="mb-3">
                        <label for="branch-name" class="form-label">Branch Name</label>
                        <input v-model="hospitalForm.bankBranchName" type="text" class="form-control" id="branch-name" placeholder="Bank Branch Name">
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-4 mb-3">
                      <div class="mb-3">
                        <label for="branch-code" class="form-label">Branch Code</label>
                        <input v-model="hospitalForm.bankBranchCode" type="text" class="form-control" id="branch-code" placeholder="Branch Code">
                      </div>
                    </div>

                    <div class="col-sm-6 col-md-4 mb-3">
                      <div class="mb-3">
                        <label for="swift-code" class="form-label">SWIFT/BIC Code</label>
                        <input v-model="hospitalForm.swiftBicCode" type="text" class="form-control" id="swift-code" placeholder="For international transfers">
                      </div>
                    </div>

                    <!-- Additional Information -->
                    <div class="col-12">
                      <div class="bg-secondary border-2 bg-soft p-3 mb-4 d-flex align-items-center">
                        <i class="mdi mdi-information me-2 fs-5"></i>
                        <p class="fw-semibold mb-0 pb-0 text-uppercase">Additional Information</p>
                      </div>
                    </div>

                    <div class="col-12 mb-3">
                      <div class="mb-3">
                        <label for="operating-hours" class="form-label">Operating Hours</label>
                        <textarea v-model="hospitalForm.operatingHours" class="form-control" id="operating-hours" rows="2" placeholder="e.g., Monday-Friday: 8:00 AM - 6:00 PM, Saturday: 8:00 AM - 2:00 PM"></textarea>
                      </div>
                    </div>

                    <div class="col-12 mb-3">
                      <div class="mb-3">
                        <label for="special-notes" class="form-label">Special Notes/Comments</label>
                        <textarea v-model="hospitalForm.specialNotes" class="form-control" id="special-notes" rows="3" placeholder="Any additional information about the hospital"></textarea>
                      </div>
                    </div>

                    <div class="col-12">
                      <div class="form-check mb-3">
                        <input v-model="hospitalForm.emergencyServices" class="form-check-input" type="checkbox" id="emergency-services">
                        <label class="form-check-label" for="emergency-services">24/7 Emergency Services Available</label>
                      </div>
                      <div class="form-check mb-3">
                        <input v-model="hospitalForm.referralCenter" class="form-check-input" type="checkbox" id="referral-center">
                        <label class="form-check-label" for="referral-center">Acts as Referral Center</label>
                      </div>
                      <div class="form-check mb-3">
                        <input v-model="hospitalForm.ambulanceAvailable" class="form-check-input" type="checkbox" id="ambulance-service">
                        <label class="form-check-label" for="ambulance-service">Ambulance Service Available</label>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="card-footer bg-white border-top py-3">
                  <div class="col-12">
                    <button
                        @click="registerHospital"
                        type="button"
                        class="btn btn-primary waves-effect waves-light text-uppercase"
                        :disabled="loading || !isFormValid"
                        :title="!isFormValid ? getFormInvalidReason() : 'Click to register hospital'">
                      <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      <i v-else class="mdi mdi-hospital-building font-size-16 align-middle me-2"></i>
                      {{ loading ? 'Registering...' : 'Register Hospital' }}
                    </button>
                    <button @click="clearForm" type="button" class="btn btn-outline-secondary waves-effect text-uppercase mx-3">
                      <i class="mdi mdi-close font-size-16 align-middle me-2"></i> Clear Form
                    </button>
                    <button @click="saveDraft" type="button" class="btn btn-outline-info waves-effect text-uppercase">
                      <i class="mdi mdi-content-save font-size-16 align-middle me-2"></i> Save Draft
                    </button>
                  </div>
                  <div v-if="!isFormValid && (validationStates.licenseNumber === 'invalid' || validationStates.hospitalEmail === 'invalid' || validationStates.primaryPhone === 'invalid')"
                       class="col-12 mt-2">
                    <small class="text-danger">
                      <i class="mdi mdi-alert-circle me-1"></i>
                      Please fix validation errors before submitting the form.
                    </small>
                  </div>
                </div>
              </div>
            </div>
            <!-- end col -->
          </div>
        </div>
        <!-- container-fluid -->
      </div>
      <!-- End Page-content -->

      <!-- Map Selection Modal -->
      <div class="modal fade" id="mapModal" tabindex="-1" role="dialog" aria-labelledby="mapModalTitle" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
        <div class="modal-dialog modal-dialog-centered modal-xl" role="document">
          <div class="modal-content">
            <div class="modal-header bg-primary text-white">
              <h5 class="modal-title text-white" id="mapModalTitle">
                <i class="mdi mdi-map-marker-plus me-2"></i>
                Select Hospital Location
              </h5>
              <button type="button" class="btn-close btn-close-white" @click="closeMapModal" aria-label="Close"></button>
            </div>
            <div class="modal-body p-0">
              <!-- Search Bar -->
              <div class="p-3 border-bottom">
                <div class="input-group">
                  <input
                      v-model="searchQuery"
                      @keyup.enter="searchLocation"
                      type="text"
                      class="form-control"
                      placeholder="Search for a location... (e.g., Kenyatta National Hospital, Nairobi)"
                      id="location-search">
                  <button class="btn btn-outline-primary" type="button" @click="searchLocation" :disabled="!googleMapsLoaded">
                    <i class="mdi mdi-magnify"></i> Search
                  </button>
                  <button class="btn btn-outline-secondary" type="button" @click="getCurrentLocation" :disabled="!googleMapsLoaded" title="Use my current location">
                    <i class="mdi mdi-crosshairs-gps"></i>
                  </button>
                </div>
                <small class="text-muted">Click on the map to place a marker at the hospital location</small>
              </div>

              <!-- Map Container -->
              <div id="map-container" style="height: 500px; position: relative;">
                <div v-if="mapLoading" class="d-flex justify-content-center align-items-center h-100">
                  <div class="text-center">
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">Loading map...</span>
                    </div>
                    <p class="mt-2 text-muted">
                      {{ googleMapsLoaded ? 'Initializing map...' : 'Loading Google Maps...' }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Selected Location Info -->
              <div v-if="tempCoordinates.lat && tempCoordinates.lng" class="p-3 bg-light border-top">
                <div class="row align-items-center">
                  <div class="col-md-8">
                    <h6 class="mb-1">Selected Location:</h6>
                    <p class="mb-0 text-muted">
                      <i class="mdi mdi-map-marker me-1"></i>
                      {{ tempLocationAddress || `${tempCoordinates.lat.toFixed(6)}, ${tempCoordinates.lng.toFixed(6)}` }}
                    </p>
                  </div>
                  <div class="col-md-4 text-end">
                    <small class="text-muted">
                      Lat: {{ tempCoordinates.lat.toFixed(6) }}<br>
                      Lng: {{ tempCoordinates.lng.toFixed(6) }}
                    </small>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="closeMapModal">
                <i class="mdi mdi-close me-2"></i>Cancel
              </button>
              <button type="button" class="btn btn-primary" @click="confirmLocation" :disabled="!tempCoordinates.lat || !tempCoordinates.lng">
                <i class="mdi mdi-check me-2"></i>Confirm Location
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Success Modal -->
      <div class="modal fade" id="successModal" tabindex="-1" role="dialog" aria-labelledby="successModalTitle" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
        <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
          <div class="modal-content">
            <div class="modal-header bg-success text-white">
              <h5 class="modal-title text-white" id="successModalTitle">
                <i class="mdi mdi-check-circle-outline me-2"></i>
                Hospital Registration Successful
              </h5>
              <button type="button" class="btn-close btn-close-white" @click="closeSuccessModal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <div class="text-center mb-4">
                <div class="avatar-lg mx-auto mb-4">
                  <div class="avatar-title bg-light text-success rounded-circle font-size-48">
                    <i class="mdi mdi-check-bold"></i>
                  </div>
                </div>
                <h4 class="mb-2">Registration Completed!</h4>
                <p class="text-muted">{{ registeredHospitalName }} has been successfully registered in the CBHI system.</p>
              </div>

              <div class="alert alert-warning d-flex align-items-center mb-4" role="alert">
                <i class="mdi mdi-alert-circle-outline font-size-24 me-3"></i>
                <div>
                  <strong>Important!</strong> Please save these API credentials securely. They will only be shown once.
                </div>
              </div>

              <div class="card border">
                <div class="card-body">
                  <h5 class="card-title mb-3">API Credentials</h5>

                  <div class="mb-3">
                    <label class="form-label text-muted small">Hospital ID</label>
                    <div class="input-group">
                      <input type="text" class="form-control bg-light" :value="apiCredentials.hospitalId" readonly>
                      <button class="btn btn-outline-secondary" type="button" @click="copyToClipboard(apiCredentials.hospitalId, 'Hospital ID')">
                        <i class="mdi mdi-content-copy"></i>
                      </button>
                    </div>
                  </div>

                  <div class="mb-3">
                    <label class="form-label text-muted small">API Key</label>
                    <div class="input-group">
                      <input type="text" class="form-control bg-light font-monospace small" :value="apiCredentials.apiKey" readonly>
                      <button class="btn btn-outline-secondary" type="button" @click="copyToClipboard(apiCredentials.apiKey, 'API Key')">
                        <i class="mdi mdi-content-copy"></i>
                      </button>
                    </div>
                  </div>

                  <div class="mb-3">
                    <label class="form-label text-muted small">API Secret</label>
                    <div class="input-group">
                      <input :type="showApiSecret ? 'text' : 'password'" class="form-control bg-light font-monospace small" :value="apiCredentials.apiSecret" readonly>
                      <button class="btn btn-outline-secondary" type="button" @click="showApiSecret = !showApiSecret">
                        <i :class="showApiSecret ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
                      </button>
                      <button class="btn btn-outline-secondary" type="button" @click="copyToClipboard(apiCredentials.apiSecret, 'API Secret')">
                        <i class="mdi mdi-content-copy"></i>
                      </button>
                    </div>
                  </div>

                  <div class="d-grid gap-2 mt-4">
                    <button class="btn btn-outline-primary" @click="downloadCredentials">
                      <i class="mdi mdi-download me-2"></i>
                      Download Credentials as Text File
                    </button>
                  </div>
                </div>
              </div>

              <div class="alert alert-info mb-0 mt-3">
                <p class="mb-0">
                  <i class="mdi mdi-information me-2"></i>
                  An email with these credentials has been sent to <strong>{{ hospitalForm.hospitalEmail || 'the registered email' }}</strong>
                </p>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="closeSuccessModal">
                <i class="mdi mdi-printer me-2"></i>
                Print Details
              </button>
              <button type="button" class="btn btn-primary" @click="registerAnother">
                <i class="mdi mdi-plus me-2"></i>
                Register Another Hospital
              </button>
              <button type="button" class="btn btn-success" @click="closeSuccessModal">
                <i class="mdi mdi-check me-2"></i>
                Done
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Copy Notification Toast -->
      <div class="position-fixed top-0 end-0 p-3" style="z-index: 11">
        <div id="copyToast" class="toast" role="alert" aria-live="assertive" aria-atomic="true">
          <div class="toast-body bg-success text-white">
            <i class="mdi mdi-check me-2"></i>
            {{ copyMessage }} copied to clipboard!
          </div>
        </div>
      </div>

      <footer class="footer">
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-6"></div>
            <div class="col-sm-6">
              <div class="text-sm-end d-sm-block">
                Ministry of Health
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
    <!-- end main content-->
  </div>
  <!-- END layout-wrapper -->

  <!-- Right bar overlay-->
  <div class="rightbar-overlay"></div>
  </body>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import { authUrl, executeJson } from "@/api";
import router from "@/router";

export default {
  name: "HospitalsAdd",
  components: { NavigationBar },
  data() {
    return {
      loading: false,

      // Google Maps API key
      googleMapsApiKey: 'AIzaSyBl3dCvpVQUs04SOTCHgITw4Ts79-dRcfI',

      // Map related data
      map: null,
      marker: null,
      mapLoading: true,
      googleMapsLoaded: false,
      searchQuery: '',
      tempCoordinates: {
        lat: null,
        lng: null
      },
      tempLocationAddress: '',
      selectedLocationAddress: '',

      // Default map center (Nairobi, Kenya - can be changed based on deployment)
      defaultMapCenter: { lat: -1.2921, lng: 36.8219 },

      // Alert states
      alert: {
        success: false,
        info: false,
        failed: false
      },
      message: '',

      // Success modal data
      showApiSecret: false,
      registeredHospitalName: '',
      apiCredentials: {
        hospitalId: '',
        apiKey: '',
        apiSecret: ''
      },
      copyMessage: '',

      // Validation states
      validationStates: {
        licenseNumber: null,
        hospitalEmail: null,
        primaryPhone: null,
        secondaryPhone: null
      },
      validationMessages: {
        licenseNumber: '',
        hospitalEmail: '',
        primaryPhone: '',
        secondaryPhone: ''
      },

      // Form data
      hospitalForm: {
        hospitalName: '',
        licenseNumber: '',
        categoryId: '',
        ownershipId: '',
        bedCapacity: null,
        countyId: '',
        subCountyId: '',
        wardId: '',
        locationId: '',
        physicalAddress: '',
        postalAddress: '',
        postalCode: '',
        poBox: '',
        latitude: null,
        longitude: null,
        primaryPhone: '',
        secondaryPhone: '',
        hospitalEmail: '',
        website: '',
        contactPerson: '',
        contactPersonPosition: '',
        bankName: '',
        bankAccountNumber: '',
        accountHolderName: '',
        bankBranchName: '',
        bankBranchCode: '',
        swiftBicCode: '',
        operatingHours: '',
        specialNotes: '',
        emergencyServices: false,
        referralCenter: false,
        ambulanceAvailable: false
      },

      // Dropdown data
      counties: [],
      subCounties: [],
      wards: [],
      locations: [],
      hospitalCategories: [],
      ownershipTypes: []
    };
  },

  computed: {
    isFormValid() {
      const requiredFieldsFilled = !!(
          this.hospitalForm.hospitalName &&
          this.hospitalForm.licenseNumber &&
          this.hospitalForm.categoryId &&
          this.hospitalForm.ownershipId &&
          this.hospitalForm.countyId &&
          this.hospitalForm.subCountyId &&
          this.hospitalForm.wardId &&
          this.hospitalForm.physicalAddress &&
          this.hospitalForm.primaryPhone &&
          this.hospitalForm.hospitalEmail &&
          this.hospitalForm.contactPerson &&
          this.hospitalForm.bankName &&
          this.hospitalForm.bankAccountNumber &&
          this.hospitalForm.accountHolderName
      );

      if (!requiredFieldsFilled) {
        return false;
      }

      const isChecking = Object.values(this.validationStates).some(state => state === 'checking');
      if (isChecking) {
        return false;
      }

      const hasValidationErrors = Object.values(this.validationStates).some(state => state === 'invalid');
      if (hasValidationErrors) {
        return false;
      }

      const validatedFields = ['licenseNumber', 'hospitalEmail', 'primaryPhone'];
      for (const field of validatedFields) {
        if (this.hospitalForm[field] && this.validationStates[field] !== 'valid') {
          if (this.validationStates[field] !== null) {
            return false;
          }
        }
      }

      if (this.hospitalForm.secondaryPhone) {
        if (this.validationStates.secondaryPhone === 'invalid' ||
            this.validationStates.secondaryPhone === 'checking') {
          return false;
        }
      }

      return true;
    }
  },

  mounted() {
    this.loadInitialData();
    this.loadDraftIfExists();
    this.loadGoogleMapsScript();
  },

  methods: {
    checkAuthentication() {
      const token = localStorage.getItem("authToken");
      if (!token) {
        router.push('/login');
      }
    },

    clearAlerts() {
      this.alert.success = false;
      this.alert.info = false;
      this.alert.failed = false;
    },

    showAlert(type, message) {
      this.clearAlerts();
      this.alert[type] = true;
      this.message = message;

      if (type === 'success' || type === 'info') {
        setTimeout(() => {
          this.clearAlerts();
        }, 5000);
      }
    },

    // Map Methods
    loadGoogleMapsScript() {
      // Check if Google Maps is already loaded
      if (window.google && window.google.maps) {
        this.googleMapsLoaded = true;
        this.mapLoading = false;
        return;
      }

      // Check if script is already loading
      if (document.getElementById('google-maps-script')) {
        return;
      }

      // Create the script element
      const script = document.createElement('script');
      script.id = 'google-maps-script';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.googleMapsApiKey}&libraries=places`;
      script.async = true;
      script.defer = true;

      // Handle script load
      script.onload = () => {
        this.googleMapsLoaded = true;
        this.mapLoading = false;
        console.log('Google Maps API loaded successfully');
      };

      // Handle script error
      script.onerror = (error) => {
        console.error('Failed to load Google Maps API:', error);
        this.mapLoading = false;
        this.showAlert('failed', 'Failed to load Google Maps. Please check your API key and internet connection.');
      };

      // Add script to document
      document.head.appendChild(script);
    },

    initMap() {
      if (!this.googleMapsLoaded || !window.google || !window.google.maps) {
        console.error('Google Maps not loaded yet');
        return;
      }

      this.map = new google.maps.Map(document.getElementById("map-container"), {
        zoom: 10,
        center: this.hospitalForm.latitude && this.hospitalForm.longitude
            ? { lat: this.hospitalForm.latitude, lng: this.hospitalForm.longitude }
            : this.defaultMapCenter,
        mapTypeControl: true,
        streetViewControl: true,
        fullscreenControl: true,
      });

      // Add click listener to map
      this.map.addListener("click", (event) => {
        this.setMarker(event.latLng.lat(), event.latLng.lng());
      });

      // Add existing marker if coordinates exist
      if (this.hospitalForm.latitude && this.hospitalForm.longitude) {
        this.setMarker(this.hospitalForm.latitude, this.hospitalForm.longitude, false);
      }

      this.mapLoading = false;
    },

    setMarker(lat, lng, isTemp = true) {
      // Remove existing marker
      if (this.marker) {
        this.marker.setMap(null);
      }

      // Create new marker
      this.marker = new google.maps.Marker({
        position: { lat: lat, lng: lng },
        map: this.map,
        draggable: true,
        title: "Hospital Location"
      });

      // Update coordinates
      if (isTemp) {
        this.tempCoordinates = { lat: lat, lng: lng };
        this.getAddressFromCoordinates(lat, lng, true);
      }

      // Add drag listener
      this.marker.addListener('dragend', (event) => {
        const newLat = event.latLng.lat();
        const newLng = event.latLng.lng();

        if (isTemp) {
          this.tempCoordinates = { lat: newLat, lng: newLng };
          this.getAddressFromCoordinates(newLat, newLng, true);
        }
      });

      // Center map on marker
      this.map.setCenter({ lat: lat, lng: lng });
    },

    showMapModal() {
      if (!this.googleMapsLoaded) {
        this.showAlert('failed', 'Google Maps is still loading. Please wait a moment and try again.');
        return;
      }

      const modal = new bootstrap.Modal(document.getElementById('mapModal'));
      modal.show();

      // Reset temp coordinates
      this.tempCoordinates = {
        lat: this.hospitalForm.latitude,
        lng: this.hospitalForm.longitude
      };
      this.tempLocationAddress = this.selectedLocationAddress;

      // Initialize map when modal is shown
      modal._element.addEventListener('shown.bs.modal', () => {
        setTimeout(() => {
          this.initMap();
        }, 100);
      });
    },

    closeMapModal() {
      const modal = bootstrap.Modal.getInstance(document.getElementById('mapModal'));
      if (modal) {
        modal.hide();
      }

      // Reset temp data
      this.tempCoordinates = { lat: null, lng: null };
      this.tempLocationAddress = '';
      this.searchQuery = '';
    },

    confirmLocation() {
      if (this.tempCoordinates.lat && this.tempCoordinates.lng) {
        this.hospitalForm.latitude = this.tempCoordinates.lat;
        this.hospitalForm.longitude = this.tempCoordinates.lng;
        this.selectedLocationAddress = this.tempLocationAddress;

        this.closeMapModal();
        this.showAlert('success', 'Location selected successfully');
      }
    },

    searchLocation() {
      if (!this.googleMapsLoaded || !this.searchQuery.trim()) {
        return;
      }

      const service = new google.maps.places.PlacesService(this.map);
      const request = {
        query: this.searchQuery,
        fields: ['name', 'geometry', 'formatted_address'],
      };

      service.textSearch(request, (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && results[0]) {
          const place = results[0];
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();

          this.setMarker(lat, lng);
          this.tempLocationAddress = place.formatted_address || place.name;

          // Center and zoom map
          this.map.setCenter({ lat: lat, lng: lng });
          this.map.setZoom(15);
        } else {
          this.showAlert('failed', 'Location not found. Please try a different search term.');
        }
      });
    },

    getCurrentLocation() {
      if (!this.googleMapsLoaded) {
        return;
      }

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
            (position) => {
              const lat = position.coords.latitude;
              const lng = position.coords.longitude;

              this.setMarker(lat, lng);
              this.map.setCenter({ lat: lat, lng: lng });
              this.map.setZoom(15);
            },
            (error) => {
              console.error('Error getting location:', error);
              this.showAlert('failed', 'Unable to get your current location. Please search for the location instead.');
            }
        );
      } else {
        this.showAlert('failed', 'Geolocation is not supported by this browser.');
      }
    },

    getAddressFromCoordinates(lat, lng, isTemp = false) {
      if (!this.googleMapsLoaded) {
        return;
      }

      const geocoder = new google.maps.Geocoder();
      const latlng = { lat: lat, lng: lng };

      geocoder.geocode({ location: latlng }, (results, status) => {
        if (status === 'OK') {
          if (results[0]) {
            if (isTemp) {
              this.tempLocationAddress = results[0].formatted_address;
            } else {
              this.selectedLocationAddress = results[0].formatted_address;
            }
          }
        } else {
          console.log('Geocoder failed due to: ' + status);
        }
      });
    },

    async loadInitialData() {
      await Promise.all([
        this.loadCounties(),
        this.loadHospitalCategories(),
        this.loadOwnershipTypes()
      ]);
    },

    async loadCounties() {
      try {
        const data = {
          function: "getCounties"
        };

        const response = await executeJson(data, authUrl);
        if (response.data.success) {
          this.counties = response.data.data.counties;
        }
      } catch (error) {
        console.error("Error loading counties:", error);
      }
    },

    async loadSubCounties() {
      if (!this.hospitalForm.countyId) {
        this.subCounties = [];
        this.wards = [];
        this.locations = [];
        return;
      }

      try {
        const data = {
          function: "getSubCounties",
          countyId: this.hospitalForm.countyId
        };

        const response = await executeJson(data, authUrl);
        if (response.data.success) {
          this.subCounties = response.data.data.subCounties;
          this.hospitalForm.subCountyId = '';
          this.hospitalForm.wardId = '';
          this.hospitalForm.locationId = '';
        }
      } catch (error) {
        console.error("Error loading sub-counties:", error);
      }
    },

    async loadWards() {
      if (!this.hospitalForm.subCountyId) {
        this.wards = [];
        this.locations = [];
        return;
      }

      try {
        const data = {
          function: "getWards",
          subCountyId: this.hospitalForm.subCountyId
        };

        const response = await executeJson(data, authUrl);
        if (response.data.success) {
          this.wards = response.data.data.wards;
          this.hospitalForm.wardId = '';
          this.hospitalForm.locationId = '';
        }
      } catch (error) {
        console.error("Error loading wards:", error);
      }
    },

    async loadLocations() {
      if (!this.hospitalForm.wardId) {
        this.locations = [];
        return;
      }

      try {
        const data = {
          function: "getLocations",
          wardId: this.hospitalForm.wardId
        };

        const response = await executeJson(data, authUrl);
        if (response.data.success) {
          this.locations = response.data.data.locations;
          this.hospitalForm.locationId = '';
        }
      } catch (error) {
        console.error("Error loading locations:", error);
      }
    },

    async loadHospitalCategories() {
      try {
        const data = {
          function: "getHospitalCategories"
        };

        const response = await executeJson(data, authUrl);
        if (response.data.success) {
          this.hospitalCategories = response.data.data.categories;
        }
      } catch (error) {
        console.error("Error loading hospital categories:", error);
      }
    },

    async loadOwnershipTypes() {
      try {
        const data = {
          function: "getOwnershipTypes"
        };

        const response = await executeJson(data, authUrl);
        if (response.data.success) {
          this.ownershipTypes = response.data.data.ownershipTypes || [];
        }
      } catch (error) {
        console.error("Error loading ownership types:", error);
      }
    },

    async validateForm() {
      const requiredFields = [
        'hospitalName',
        'licenseNumber',
        'categoryId',
        'ownershipId',
        'countyId',
        'subCountyId',
        'wardId',
        'physicalAddress',
        'primaryPhone',
        'hospitalEmail',
        'contactPerson',
        'bankName',
        'bankAccountNumber',
        'accountHolderName'
      ];

      for (const field of requiredFields) {
        if (!this.hospitalForm[field]) {
          this.showAlert('failed', `Please fill in all required fields. Missing: ${this.formatFieldName(field)}`);
          return false;
        }
      }

      if (!this.isValidEmail(this.hospitalForm.hospitalEmail)) {
        this.showAlert('failed', 'Please enter a valid email address');
        return false;
      }

      if (!this.isValidPhone(this.hospitalForm.primaryPhone)) {
        this.showAlert('failed', 'Please enter a valid phone number');
        return false;
      }

      this.hospitalForm.primaryPhone = this.formatPhoneNumber(this.hospitalForm.primaryPhone);

      if (this.hospitalForm.secondaryPhone) {
        if (!this.isValidPhone(this.hospitalForm.secondaryPhone)) {
          this.showAlert('failed', 'Secondary phone number must be a valid phone number');
          return false;
        }
        this.hospitalForm.secondaryPhone = this.formatPhoneNumber(this.hospitalForm.secondaryPhone);
      }

      const licenseAvailable = await this.checkLicenseAvailability();
      if (!licenseAvailable) {
        return false;
      }

      const emailAvailable = await this.checkEmailAvailability();
      if (!emailAvailable) {
        return false;
      }

      const phoneAvailable = await this.checkPhoneAvailability();
      if (!phoneAvailable) {
        return false;
      }

      return true;
    },

    getFormInvalidReason() {
      if (this.validationStates.licenseNumber === 'invalid') {
        return 'License number is already registered';
      }
      if (this.validationStates.hospitalEmail === 'invalid') {
        return 'Email is invalid or already registered';
      }
      if (this.validationStates.primaryPhone === 'invalid') {
        return 'Primary phone is invalid or already registered';
      }
      if (this.validationStates.secondaryPhone === 'invalid') {
        return 'Secondary phone is invalid or already registered';
      }

      if (Object.values(this.validationStates).some(state => state === 'checking')) {
        return 'Validating fields...';
      }

      if (!this.hospitalForm.hospitalName) return 'Hospital name is required';
      if (!this.hospitalForm.licenseNumber) return 'License number is required';
      if (!this.hospitalForm.categoryId) return 'Hospital category is required';
      if (!this.hospitalForm.ownershipId) return 'Ownership type is required';
      if (!this.hospitalForm.countyId) return 'County is required';
      if (!this.hospitalForm.subCountyId) return 'Sub-County is required';
      if (!this.hospitalForm.wardId) return 'Ward is required';
      if (!this.hospitalForm.physicalAddress) return 'Physical address is required';
      if (!this.hospitalForm.primaryPhone) return 'Primary phone is required';
      if (!this.hospitalForm.hospitalEmail) return 'Hospital email is required';
      if (!this.hospitalForm.contactPerson) return 'Contact person is required';
      if (!this.hospitalForm.bankName) return 'Bank name is required';
      if (!this.hospitalForm.bankAccountNumber) return 'Bank account number is required';
      if (!this.hospitalForm.accountHolderName) return 'Account holder name is required';

      return 'Please complete all required fields';
    },

    formatFieldName(fieldName) {
      return fieldName
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, str => str.toUpperCase())
          .trim();
    },

    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    isValidPhone(phone) {
      if (!phone) return false;

      const cleanPhone = phone.replace(/[\s()-]/g, '');

      // Generic phone validation - accepts various formats
      // Minimum 7 digits, maximum 15 digits (international standard)
      // Can start with + for international format
      const phoneRegex = /^\+?\d{7,15}$/;

      return phoneRegex.test(cleanPhone);
    },

    formatPhoneNumber(phone) {
      if (!phone) return '';

      // Remove all non-digit characters except +
      let cleanPhone = phone.replace(/[^\d+]/g, '');

      // Keep the + if present at the start
      if (cleanPhone.startsWith('+')) {
        return cleanPhone;
      }

      // Return as-is if no specific formatting needed
      return cleanPhone;
    },

    async checkLicenseAvailability() {
      if (!this.hospitalForm.licenseNumber) {
        this.validationStates.licenseNumber = null;
        this.validationMessages.licenseNumber = '';
        return true;
      }

      this.validationStates.licenseNumber = 'checking';
      this.validationMessages.licenseNumber = 'Checking availability...';

      try {
        const data = {
          function: "checkHospitalLicenseAvailability",
          licenseNumber: this.hospitalForm.licenseNumber
        };

        const response = await executeJson(data, authUrl);
        const responseData = response.data?.data || response.data;

        if (responseData && !responseData.isAvailable) {
          this.validationStates.licenseNumber = 'invalid';
          this.validationMessages.licenseNumber = responseData.existingHospital
              ? `Already registered to: ${responseData.existingHospital}`
              : 'License number is not available';
          return false;
        }

        this.validationStates.licenseNumber = 'valid';
        this.validationMessages.licenseNumber = responseData.message || 'License number is available';
        return true;
      } catch (error) {
        console.error("Error checking license availability:", error);
        this.validationStates.licenseNumber = null;
        this.validationMessages.licenseNumber = '';
        return true;
      }
    },

    async checkEmailAvailability() {
      if (!this.hospitalForm.hospitalEmail) {
        this.validationStates.hospitalEmail = null;
        this.validationMessages.hospitalEmail = '';
        return true;
      }

      if (!this.isValidEmail(this.hospitalForm.hospitalEmail)) {
        this.validationStates.hospitalEmail = 'invalid';
        this.validationMessages.hospitalEmail = 'Invalid email format';
        return false;
      }

      this.validationStates.hospitalEmail = 'checking';
      this.validationMessages.hospitalEmail = 'Checking availability...';

      try {
        const data = {
          function: "checkHospitalEmailAvailability",
          email: this.hospitalForm.hospitalEmail
        };

        const response = await executeJson(data, authUrl);
        const responseData = response.data?.data || response.data;

        if (responseData && !responseData.isAvailable) {
          this.validationStates.hospitalEmail = 'invalid';
          this.validationMessages.hospitalEmail = responseData.existingHospital
              ? `Already registered to: ${responseData.existingHospital}`
              : 'Email is not available';
          return false;
        }

        this.validationStates.hospitalEmail = 'valid';
        this.validationMessages.hospitalEmail = responseData.message || 'Email is available';
        return true;
      } catch (error) {
        console.error("Error checking email availability:", error);
        this.validationStates.hospitalEmail = null;
        this.validationMessages.hospitalEmail = '';
        return true;
      }
    },

    async checkPhoneAvailability(isSecondary = false) {
      const phoneField = isSecondary ? 'secondaryPhone' : 'primaryPhone';
      const phoneValue = this.hospitalForm[phoneField];

      if (!phoneValue) {
        this.validationStates[phoneField] = null;
        this.validationMessages[phoneField] = '';
        return true;
      }

      if (!this.isValidPhone(phoneValue)) {
        this.validationStates[phoneField] = 'invalid';
        this.validationMessages[phoneField] = 'Invalid phone format (minimum 7 digits)';
        return false;
      }

      const formattedPhone = this.formatPhoneNumber(phoneValue);
      this.hospitalForm[phoneField] = formattedPhone;

      this.validationStates[phoneField] = 'checking';
      this.validationMessages[phoneField] = 'Checking availability...';

      try {
        const data = {
          function: "checkHospitalPhoneAvailability",
          phone: formattedPhone
        };

        const response = await executeJson(data, authUrl);
        const responseData = response.data?.data || response.data;

        if (responseData && !responseData.isAvailable) {
          this.validationStates[phoneField] = 'invalid';
          this.validationMessages[phoneField] = responseData.existingHospital
              ? `Already registered to: ${responseData.existingHospital}`
              : 'Phone number is not available';
          return false;
        }

        this.validationStates[phoneField] = 'valid';
        this.validationMessages[phoneField] = responseData.message || 'Phone number is available';
        return true;
      } catch (error) {
        console.error("Error checking phone availability:", error);
        this.validationStates[phoneField] = null;
        this.validationMessages[phoneField] = '';
        return true;
      }
    },

    async registerHospital() {
      const isValid = await this.validateForm();
      if (!isValid) {
        return;
      }

      this.loading = true;
      this.showAlert('info', 'Registering hospital...');

      try {
        const data = {
          function: "registerHospital",
          ...this.hospitalForm,
          collectionPoint: sessionStorage.getItem('hospitalName') || 'CBHI System'
        };

        const response = await executeJson(data, authUrl);

        if (response.data.success) {
          this.showAlert('success', response.data.message);

          if (response.data.data) {
            this.registeredHospitalName = this.hospitalForm.hospitalName;
            this.apiCredentials = {
              hospitalId: response.data.data.hospitalId,
              apiKey: response.data.data.apiKey,
              apiSecret: response.data.data.apiSecret
            };

            sessionStorage.setItem('lastRegisteredHospitalId', response.data.data.hospitalId);
            this.showSuccessModal();
          }

          setTimeout(() => {
            this.clearForm();
            this.clearDraft();
          }, 500);

        } else {
          this.showAlert('failed', response.data.message);
        }
      } catch (error) {
        this.showAlert('failed', error.message || 'Failed to register hospital');
      } finally {
        this.loading = false;
      }
    },

    clearForm() {
      this.hospitalForm = {
        hospitalName: '',
        licenseNumber: '',
        categoryId: '',
        ownershipId: '',
        bedCapacity: null,
        countyId: '',
        subCountyId: '',
        wardId: '',
        locationId: '',
        physicalAddress: '',
        postalAddress: '',
        postalCode: '',
        poBox: '',
        latitude: null,
        longitude: null,
        primaryPhone: '',
        secondaryPhone: '',
        hospitalEmail: '',
        website: '',
        contactPerson: '',
        contactPersonPosition: '',
        bankName: '',
        bankAccountNumber: '',
        accountHolderName: '',
        bankBranchName: '',
        bankBranchCode: '',
        swiftBicCode: '',
        operatingHours: '',
        specialNotes: '',
        emergencyServices: false,
        referralCenter: false,
        ambulanceAvailable: false
      };

      this.validationStates = {
        licenseNumber: null,
        hospitalEmail: null,
        primaryPhone: null,
        secondaryPhone: null
      };

      this.validationMessages = {
        licenseNumber: '',
        hospitalEmail: '',
        primaryPhone: '',
        secondaryPhone: ''
      };

      this.subCounties = [];
      this.wards = [];
      this.locations = [];
      this.selectedLocationAddress = '';
      this.clearAlerts();

      // Clear map marker
      if (this.marker) {
        this.marker.setMap(null);
        this.marker = null;
      }
    },

    saveDraft() {
      const draftData = {
        ...this.hospitalForm,
        selectedLocationAddress: this.selectedLocationAddress
      };
      localStorage.setItem('hospitalFormDraft', JSON.stringify(draftData));
      this.showAlert('success', 'Draft saved successfully');
    },

    loadDraftIfExists() {
      const draft = localStorage.getItem('hospitalFormDraft');
      if (draft) {
        try {
          const draftData = JSON.parse(draft);
          this.hospitalForm = { ...this.hospitalForm, ...draftData };
          this.selectedLocationAddress = draftData.selectedLocationAddress || '';

          if (this.hospitalForm.countyId) {
            this.loadSubCounties();
          }
          if (this.hospitalForm.subCountyId) {
            this.loadWards();
          }
          if (this.hospitalForm.wardId) {
            this.loadLocations();
          }

          this.showAlert('info', 'Draft loaded');
        } catch (error) {
          console.error("Error loading draft:", error);
        }
      }
    },

    clearDraft() {
      localStorage.removeItem('hospitalFormDraft');
    },

    showSuccessModal() {
      const modal = new bootstrap.Modal(document.getElementById('successModal'));
      modal.show();
    },

    closeSuccessModal() {
      const modal = bootstrap.Modal.getInstance(document.getElementById('successModal'));
      if (modal) {
        modal.hide();
      }
    },

    registerAnother() {
      this.closeSuccessModal();
      this.clearForm();
      this.apiCredentials = {
        hospitalId: '',
        apiKey: '',
        apiSecret: ''
      };
      this.registeredHospitalName = '';
    },

    copyToClipboard(text, label) {
      navigator.clipboard.writeText(text).then(() => {
        this.copyMessage = label;
        const toastEl = document.getElementById('copyToast');
        const toast = new bootstrap.Toast(toastEl, {
          delay: 2000
        });
        toast.show();
      }).catch(err => {
        console.error('Failed to copy: ', err);
      });
    },

    downloadCredentials() {
      const credentials = `CBHI Hospital Registration Credentials
========================================

Hospital Name: ${this.registeredHospitalName}
Registration Date: ${new Date().toLocaleDateString()}

API Credentials:
----------------
Hospital ID: ${this.apiCredentials.hospitalId}
API Key: ${this.apiCredentials.apiKey}
API Secret: ${this.apiCredentials.apiSecret}

IMPORTANT: Please save these credentials securely.
The API Secret will not be shown again.

For technical support, contact: support@cbhi.go.ke
`;

      const blob = new Blob([credentials], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CBHI_Hospital_${this.apiCredentials.hospitalId}_Credentials.txt`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }
  }
};
</script>

<style scoped>
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.alert {
  margin-bottom: 1rem;
}

.form-check-input:checked {
  background-color: #5156be;
  border-color: #5156be;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.font-monospace {
  font-family: 'Courier New', Courier, monospace;
}

.avatar-lg {
  width: 5rem;
  height: 5rem;
}

.avatar-title {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-header.bg-success {
  background-color: #34c38f !important;
}

.modal-header.bg-primary {
  background-color: #5156be !important;
}

.btn-close-white {
  filter: invert(1) grayscale(100%) brightness(200%);
}

.toast {
  min-width: 250px;
}

.btn-link {
  text-decoration: none;
  color: #5156be;
}

.btn-link:hover {
  text-decoration: underline;
}

.valid-feedback, .invalid-feedback {
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.valid-feedback {
  color: #34c38f;
}

.invalid-feedback {
  color: #f46a6a;
}

.form-control.is-valid {
  border-color: #34c38f;
  padding-right: calc(1.5em + 0.75rem);
  background-image: none;
}

.form-control.is-invalid {
  border-color: #f46a6a;
  padding-right: calc(1.5em + 0.75rem);
  background-image: none;
}

.input-group .form-control.is-valid,
.input-group .form-control.is-invalid {
  padding-right: 0.75rem;
}

.input-group-text {
  background-color: #f8f9fa;
  border: 1px solid #ced4da;
}

.input-group .form-control.is-valid ~ .input-group-text,
.input-group .form-control.is-valid ~ .input-group-append .input-group-text {
  border-color: #34c38f;
}

.input-group .form-control.is-invalid ~ .input-group-text,
.input-group .form-control.is-invalid ~ .input-group-append .input-group-text {
  border-color: #f46a6a;
}

/* Map specific styles */
#map-container {
  width: 100%;
  height: 500px;
}

.modal-xl {
  max-width: 90%;
}

@media (max-width: 768px) {
  .modal-xl {
    max-width: 95%;
  }

  #map-container {
    height: 400px;
  }
}
</style>